import { Component, EventEmitter, computed, inject, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { trigger, transition, style, animate } from '@angular/animations';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '@modules/auth/services/auth.service';
import { Destroyable } from '@shared/utils';
import { ConversationsBadgeComponent } from '@modules/conversations/components/conversations-badge/conversations-badge.component';
import { ConversationsSseService } from '@modules/conversations/services/conversations-sse.service';
import { EntitlementsStore } from '@modules/entitlements/services';
import { UpgradeService } from '@app/shared/services/upgrade.service';
import { CapyStatsService } from '@shared/services/capystats.service';
import { PlanBadgeComponent } from '@app/shared/components/plan-badge/plan-badge.component';
import { SidebarMenuItem } from '../models';
import { SidebarMenuService } from '../services';

@Component({
  standalone: true,
  selector: 'app-dashboard-sidebar',
  templateUrl: './dashboard-sidebar.component.html',
  styleUrl: './dashboard-sidebar.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    RippleModule,
    AvatarModule,
    TooltipModule,
    TranslocoModule,
    ConversationsBadgeComponent,
    PlanBadgeComponent
  ],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('150ms ease-in', style({ height: 0, opacity: 0 }))
      ])
    ])
  ]
})
export class DashboardSidebarComponent extends Destroyable implements OnInit {
  @Input() isMobile = false;
  @Input() collapsed = false;
  @Input() hideLogoSection = false;
  @Output() menuItemClick = new EventEmitter<void>();
  @Output() collapseToggle = new EventEmitter<void>();

  private router = inject(Router);
  private authService = inject(AuthService);
  private sidebarMenuService = inject(SidebarMenuService);
  private entitlements = inject(EntitlementsStore);
  private upgradeService = inject(UpgradeService);
  private readonly capyStats = inject(CapyStatsService);
  readonly sseService = inject(ConversationsSseService);

  /** Current plan key (e.g. 'free' | 'pro'); null until entitlements load. */
  readonly plan = this.entitlements.plan;

  /** Free (or not-yet-loaded) plans get an in-menu "upgrade" CTA. */
  readonly isFreePlan = computed(() => {
    const plan = this.plan();
    return !plan || plan === 'free';
  });

  loading = signal(false);
  userMenuExpanded = false;
  menuItems: SidebarMenuItem[] = [];

  /** Current user's role, for role-gated menu items. */
  readonly role = signal<string | null>(null);

  userInfo = {
    name: '',
    status: 'Premium',
    avatar: ''
  };

  constructor() {
    super();
    this.menuItems = this.sidebarMenuService.getStaticMenu();
    this.authService.currentUser$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(user => {
        this.role.set(user?.role ?? null);
        if (user) {
          const fullName = user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.firstName;
          const initials = user.lastName
            ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
            : user.firstName.charAt(0).toUpperCase();
          this.userInfo = {
            name: fullName,
            status: 'Premium',
            avatar: initials
          };
        }
      });
  }

  ngOnInit(): void {
    this.loadMenu();
    this.setupRouteListener();
  }

  private loadMenu(): void {
    this.loading.set(true);

    this.sidebarMenuService.getMenu()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (items) => {
          this.menuItems = items;
          this.updateActiveStateFromRoute(this.router.url);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        }
      });
  }

  private setupRouteListener(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroyed$)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateActiveStateFromRoute(event.urlAfterRedirects);
      });
  }

  /**
   * Plan-gating for menu items (UX only — backend still enforces access).
   * Items without `requiresAnyFeature` are always visible. Feature-gated items
   * stay hidden until entitlements confirm at least one required feature, so a
   * paid tab never flashes for users who don't have it.
   */
  isItemVisible(item: SidebarMenuItem): boolean {
    // Role gate (e.g. super_admin-only while testing) — applied first.
    if (item.requiresRole?.length && !item.requiresRole.includes(this.role() ?? '')) {
      return false;
    }
    if (item.requiresAnyFeature?.length) {
      return this.entitlements.hasAnyFeature(item.requiresAnyFeature);
    }
    return true;
  }

  onMenuItemClick(item: SidebarMenuItem): void {
    // NO-PII: send only the route path. `item.label` is a stable i18n key for
    // static items but raw user/tenant text for dynamic (backend) menu items
    // (academy/project/child names), so it can carry PII — never forward it.
    // The route uniquely identifies the section (slug/id only, no free text).
    if (item.route) {
      this.capyStats.track('sidebar_section_opened', { section: item.route });
    }

    if (item.route) {
      this.router.navigate([item.route]);
      this.clearActiveStates();
      item.active = true;
    }

    if (item.command) {
      item.command();
    }

    if (this.isMobile) {
      this.menuItemClick.emit();
    }
  }

  toggleCategory(item: SidebarMenuItem): void {
    item.expanded = !item.expanded;
  }

  toggleExpand(item: SidebarMenuItem, event: Event): void {
    event.stopPropagation();
    item.expanded = !item.expanded;
  }

  onNewAcademy(): void {
    console.log('Tworzenie nowej akademii...');
  }

  onCollapseToggle(): void {
    this.collapseToggle.emit();
  }

  toggleUserMenu(): void {
    this.userMenuExpanded = !this.userMenuExpanded;
  }

  onLegalLinkClick(type: 'privacy-policy' | 'cookies'): void {
    this.router.navigate([`/${type}`]);
    if (this.isMobile) {
      this.menuItemClick.emit();
    }
  }

  onUpgrade(): void {
    this.userMenuExpanded = false;
    this.upgradeService.goToPlans();

    if (this.isMobile) {
      this.menuItemClick.emit();
    }
  }

  onUserMenuAction(action: 'profile' | 'settings' | 'logout'): void {
    this.userMenuExpanded = false;

    switch (action) {
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      case 'settings':
        this.router.navigate(['/settings']);
        break;
      case 'logout':
        this.authService.logout();
        break;
    }

    if (this.isMobile) {
      this.menuItemClick.emit();
    }
  }

  private clearActiveStates(): void {
    const clearRecursive = (items: SidebarMenuItem[]) => {
      items.forEach(item => {
        item.active = false;
        if (item.children?.length) {
          clearRecursive(item.children);
        }
      });
    };
    clearRecursive(this.menuItems);
  }

  private updateActiveStateFromRoute(url: string): void {
    this.clearActiveStates();

    const findAndActivate = (items: SidebarMenuItem[], parents: SidebarMenuItem[] = []): boolean => {
      for (const item of items) {
        if (item.children?.length) {
          const childFound = findAndActivate(item.children, [...parents, item]);
          if (childFound) {
            return true;
          }
        }

        const isMatch = item.route && (
          url === item.route ||
          url.startsWith(item.route + '/')
        );

        if (isMatch) {
          item.active = true;
          parents.forEach(parent => parent.expanded = true);

          if (item.children?.length) {
            item.expanded = true;
          }
          return true;
        }
      }
      return false;
    };

    findAndActivate(this.menuItems);
  }
}
